// Copyright (c) Docugami, Inc. All rights reserved.

import React from "react";
import { createTheme, loadTheme } from "@fluentui/react";
import { dgFontFamily, primaryFontExpanded } from "components/common/FontConstants";
import { AppTheme, LogoComponentProps, ThemeColors } from "../ThemeTypes";

/**
 * Blue Steel brand colors - Navy blue theme
 */
export const blueSteelColors: ThemeColors = {
    // Primary brand colors - Navy blue
    primary: '#1E3A5F',
    primaryDark: '#152A45',
    primaryLight: '#3A5A8A',
    primaryAccent: '#1E3A5F',

    // Action colors
    actionBlue: '#0066CC',
    lightActionBlue: '#0066CC80',

    // Semantic colors
    success: '#28A745',
    warning: '#FFC107',
    error: '#DC3545',
    info: '#17A2B8',

    // Header colors
    headerBackground: '#1E3A5F',
    headerText: '#FFFFFF',

    // UI colors
    backgroundWhite: '#F8FAFB',
    backgroundGray: '#E8EDF2',
    backgroundPanel: '#F0F4F8',
    landingPageBackground: '#F5F7FA',

    // Text colors
    textPrimary: '#2C3E50',
    textSecondary: '#6C7A89',
    textDisabled: '#A0AEC0',

    // Status colors
    confirmed: '#90EE90',
    unconfirmed: '#FFE4B5',
    highlight: '#4A90D9',

    // Component-specific colors
    chunkSelected: '#4A90D9BF',
    chunkHover: '#A8C5E280',
    machineGreen: '#68D391',
    humanBlue: '#4299E1',
};

const blueSteelPrimaryThemeSettings = {
    defaultFontStyle: { fontFamily: primaryFontExpanded },
    palette: {
        themePrimary: blueSteelColors.primaryAccent,
        themeLighterAlt: '#f3f6f9',
        themeLighter: '#d0dbe7',
        themeLight: '#aac0d4',
        themeTertiary: '#6690ab',
        themeSecondary: '#366385',
        themeDarkAlt: '#1b3455',
        themeDark: '#172c48',
        themeDarker: '#112035',
        neutralLighterAlt: '#faf9f8',
        neutralLighter: '#f3f2f1',
        neutralLight: '#edebe9',
        neutralQuaternaryAlt: '#e1dfdd',
        neutralQuaternary: '#d0d0d0',
        neutralTertiaryAlt: '#c8c6c4',
        neutralTertiary: '#a19f9d',
        neutralSecondary: '#605e5c',
        neutralPrimaryAlt: '#3b3a39',
        neutralPrimary: '#323130',
        neutralDark: '#201f1e',
        black: '#000000',
        white: '#ffffff',
    }
};

const blueSteelSecondaryThemeSettings = {
    defaultFontStyle: { fontFamily: "'" + dgFontFamily + "', sans-serif" },
    palette: {
        themePrimary: blueSteelColors.primaryAccent,
        themeLighterAlt: '#f5f7fa',
        themeLighter: '#dce4ed',
        themeLight: '#c1cfdf',
        themeTertiary: '#8da6c0',
        themeSecondary: '#6482a3',
        themeDarkAlt: '#1b3455',
        themeDark: '#172c48',
        themeDarker: '#112035',
        neutralLighterAlt: '#eeeeee',
        neutralLighter: '#eaeaea',
        neutralLight: '#e1e1e1',
        neutralQuaternaryAlt: '#d1d1d1',
        neutralQuaternary: '#c8c8c8',
        neutralTertiaryAlt: '#c0c0c0',
        neutralTertiary: '#a19f9d',
        neutralSecondary: '#605e5c',
        neutralPrimaryAlt: '#3b3a39',
        neutralPrimary: '#323130',
        neutralDark: '#201f1e',
        black: '#000000',
        white: '#f6f6f6',
    }
};

const blueSteelHeaderThemeSettings = {
    defaultFontStyle: { fontFamily: primaryFontExpanded },
    palette: {
        themePrimary: '#ffffff',
        themeLighterAlt: '#767676',
        themeLighter: '#a6a6a6',
        themeLight: '#c8c8c8',
        themeTertiary: '#d0d0d0',
        themeSecondary: '#dadada',
        themeDarkAlt: '#eaeaea',
        themeDark: '#f4f4f4',
        themeDarker: '#f8f8f8',
        neutralLighterAlt: '#2a4a70',
        neutralLighter: '#28476c',
        neutralLight: '#264367',
        neutralQuaternaryAlt: '#233e5f',
        neutralQuaternary: '#213a5a',
        neutralTertiaryAlt: '#1f3756',
        neutralTertiary: '#c8c8c8',
        neutralSecondary: '#d0d0d0',
        neutralPrimaryAlt: '#dadada',
        neutralPrimary: '#ffffff',
        neutralDark: '#f4f4f4',
        black: '#f8f8f8',
        white: blueSteelColors.headerBackground,
    }
};

/**
 * Blue Steel Logo - Text only version
 */
export const BlueSteelTextLogo: React.FC<LogoComponentProps> = ({
    width = 120,
    height = 24,
    color = '#FFFFFF'
}) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 120 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <text
                x="0"
                y="18"
                fontFamily="Montserrat, sans-serif"
                fontSize="18"
                fontWeight="700"
                fill={color}
            >
                Blue Steel
            </text>
        </svg>
    );
};

/**
 * Blue Steel Logo - Circle mark
 */
export const BlueSteelLogoWithMark: React.FC<LogoComponentProps> = ({
    width = 24,
    height = 24,
    color = '#FFFFFF'
}) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Simple circle */}
            <circle cx="12" cy="12" r="10" fill={color} />
        </svg>
    );
};

/**
 * Blue Steel theme configuration
 */
export const blueSteelTheme: AppTheme = {
    id: 'bluesteel',
    name: 'Blue Steel',
    brand: {
        productName: 'Blue Steel',
        companyName: 'Blue Steel',
        shortName: 'BS',
    },
    logo: {
        TextLogoComponent: BlueSteelTextLogo,
        LogoWithMarkComponent: BlueSteelLogoWithMark,
        faviconUrl: '/favicon-bluesteel.svg',
    },
    colors: blueSteelColors,
    fluentPrimaryTheme: loadTheme(blueSteelPrimaryThemeSettings),
    fluentSecondaryTheme: createTheme(blueSteelSecondaryThemeSettings),
    fluentHeaderTheme: createTheme(blueSteelHeaderThemeSettings),
};
